import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dossiersAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import AppLayout from '../components/AppLayout';
import { THEMES, DEFAULT_TEMPLATE } from '../lib/dossierThemes';

// ─── Helpers ────────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  'linear-gradient(135deg,var(--vio),#2563EB)',
  'linear-gradient(135deg,#059669,#0284C7)',
  'linear-gradient(135deg,#DC2626,#7C3AED)',
  'linear-gradient(135deg,#D97706,#EA580C)',
  'linear-gradient(135deg,#0891B2,#4338CA)',
  'linear-gradient(135deg,#BE185D,#7C3AED)',
];
const avatarColor = (name = '') =>
  AVATAR_COLORS[Math.max(0, name.charCodeAt(0)) % AVATAR_COLORS.length];

const generateTrigram = (fullName) => {
  if (!fullName) return '?';
  const parts = fullName.trim().split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0].substring(0, 3).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1].substring(0, 2)).toUpperCase();
};

const formatRelDate = (date) => {
  if (!date) return '—';
  const now = new Date();
  const d = new Date(date);
  const diff = Math.floor((now - d) / 86400000);
  if (diff === 0) return "aujourd'hui";
  if (diff === 1) return 'hier';
  if (diff < 7) return `il y a ${diff} jours`;
  if (diff < 30) return `il y a ${Math.floor(diff / 7)} sem.`;
  if (diff < 365) return `il y a ${Math.floor(diff / 30)} mois`;
  return d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' });
};

// ─── Icons ──────────────────────────────────────────────────────────────────
const IconDownload = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M6.5 1v7M3.5 5.5l3 3 3-3M1 10v.5A1.5 1.5 0 002.5 12h8a1.5 1.5 0 001.5-1.5V10"/>
  </svg>
);
const IconEdit = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M9 2l2 2-6.5 6.5L2 12l1.5-2.5L9 2z"/>
  </svg>
);
const IconTrash = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M2 3.5h9M5 3.5V2.5a.5.5 0 01.5-.5h2a.5.5 0 01.5.5v1M4 3.5L4.5 11h4L9 3.5"/>
  </svg>
);

// ─── Library page ───────────────────────────────────────────────────────────
const Library = () => {
  const { user, currentOrg, orgLoading, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [dossiers, setDossiers] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) navigate('/login', { replace: true });
  }, [user, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    setDataLoading(true);
    try {
      const data = await dossiersAPI.getAll();
      setDossiers(data);
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de charger les dossiers', variant: 'destructive' });
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentOrg) fetchData();
    else if (!authLoading) setDataLoading(false);
  }, [currentOrg, authLoading, fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce dossier ?')) return;
    try {
      await dossiersAPI.delete(id);
      setDossiers((prev) => prev.filter((d) => d.id !== id));
      toast({ title: 'Dossier supprimé', description: 'Le dossier a été supprimé avec succès.' });
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de supprimer le dossier.', variant: 'destructive' });
    }
  };

  const startRename = (dossier, e) => {
    e.stopPropagation();
    setEditingId(dossier.id);
    setEditingName(dossier.consultant_name || dossier.name || '');
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingName('');
  };

  const saveRename = async (dossier) => {
    const newName = editingName.trim();
    const current = dossier.consultant_name || dossier.name || '';
    if (!newName || newName === current) {
      cancelRename();
      return;
    }
    setSavingId(dossier.id);
    try {
      const updated = await dossiersAPI.update(dossier.id, {
        consultant_name: newName,
        consultant_title: dossier.consultant_title || '',
      });
      setDossiers((prev) => prev.map((d) => (d.id === dossier.id ? { ...d, ...updated } : d)));
      toast({ title: 'Nom mis à jour', description: 'Le nom du consultant a été modifié.' });
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de modifier le nom.', variant: 'destructive' });
    } finally {
      setSavingId(null);
      setEditingId(null);
      setEditingName('');
    }
  };

  const handleDownloadPDF = async (dossier) => {
    try {
      await dossiersAPI.downloadPDF(dossier.id, dossier.consultant_name || dossier.name);
      toast({ title: 'PDF téléchargé', description: 'Le PDF a été téléchargé avec succès.' });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Impossible de télécharger le PDF.';
      toast({ title: 'Erreur PDF', description: detail, variant: 'destructive' });
    }
  };

  const filteredDossiers = dossiers.filter((d) => {
    const q = searchQuery.toLowerCase();
    return (
      !q ||
      (d.consultant_name || d.name || '').toLowerCase().includes(q) ||
      (d.consultant_title || '').toLowerCase().includes(q) ||
      JSON.stringify(d.competences || '').toLowerCase().includes(q)
    );
  });

  if (authLoading || orgLoading) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{`@keyframes sd-spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 40, height: 40, borderRadius: '50%', border: '2.5px solid var(--border)', borderTopColor: 'var(--vio)', animation: 'sd-spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <AppLayout
      title="Bibliothèque DC"
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      dossiersCount={dossiers.length}
    >
      {/* ── Section header ── */}
      <div className="sd-section-head">
        <div>
          <div className="sd-section-title">Dossiers de Compétences</div>
          <div className="sd-section-sub">
            {dataLoading ? '…' : `${filteredDossiers.length} dossier${filteredDossiers.length !== 1 ? 's' : ''}${searchQuery ? ` sur ${dossiers.length}` : ''}`}
          </div>
        </div>
        <button className="sd-btn sd-btn-primary" onClick={() => navigate('/dossier/new')}>
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M5.5 1v9M1 5.5h9"/>
          </svg>
          Nouveau dossier
        </button>
      </div>

      {/* ── Filter tabs ── */}
      <div className="sd-toolbar">
        <div className="sd-filter-tabs">
          {[
            { key: 'all', label: 'Tous' },
            { key: 'draft', label: 'Brouillons' },
            { key: 'ready', label: 'Prêts' },
            { key: 'archived', label: 'Archivés' },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`sd-filter-tab${activeTab === key ? ' active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              {label}
              {key === 'all' && <span className="count">{dossiers.length}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* ── Grid ── */}
      {dataLoading ? (
        <div className="sd-profile-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="sd-profile-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="sd-skeleton" style={{ height: 14, width: '70%' }} />
              <div className="sd-skeleton" style={{ height: 11, width: '50%' }} />
              <div className="sd-skeleton" style={{ height: 11, width: '60%' }} />
              <div className="sd-skeleton" style={{ height: 11, width: '40%' }} />
            </div>
          ))}
        </div>
      ) : filteredDossiers.length === 0 ? (
        <div className="sd-lib-empty">
          <div className="sd-lib-empty-icon">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="var(--vio2)" strokeWidth="1.5" strokeLinecap="round">
              <rect x="4" y="8" width="20" height="18" rx="3"/>
              <path d="M10 8V6a4 4 0 018 0v2M8 14h12M8 18.5h8"/>
            </svg>
          </div>
          <div className="sd-lib-empty-title">
            {searchQuery ? 'Aucun résultat pour cette recherche' : 'Aucun dossier pour le moment'}
          </div>
          <div className="sd-lib-empty-sub">
            {searchQuery
              ? "Essayez avec d'autres mots-clés."
              : 'Créez votre premier dossier ou importez un CV pour démarrer.'}
          </div>
          {!searchQuery && (
            <button className="sd-btn sd-btn-primary" onClick={() => navigate('/dossier/new')} style={{ margin: '0 auto' }}>
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M5.5 1v9M1 5.5h9"/>
              </svg>
              Créer un dossier
            </button>
          )}
        </div>
      ) : (
        <div className="sd-profile-grid sd-stagger">
          {filteredDossiers.map((dossier) => {
            const name = dossier.consultant_name || dossier.name || '';
            const trigram = generateTrigram(name);
            const bg = avatarColor(name);

            const rawComps = dossier.competences;
            const domainGroups = Array.isArray(rawComps) && rawComps.length > 0 && typeof rawComps[0] === 'object' && rawComps[0].domain
              ? rawComps : null;
            const comps = domainGroups
              ? domainGroups.flatMap(g => (g.tags || []).map(t => String(t)))
              : Array.isArray(rawComps) ? rawComps.map(c => String(c)) : [];

            const expCount = Array.isArray(dossier.experiences) ? dossier.experiences.length : 0;
            const forCount = Array.isArray(dossier.formations) ? dossier.formations.length : 0;

            const lastTouched = dossier.updated_at || dossier.created_at;
            const lastTouchedLabel = dossier.updated_at ? `Modifié ${formatRelDate(dossier.updated_at)}` : `Créé ${formatRelDate(dossier.created_at)}`;

            const tmpl = THEMES[dossier.template_id] || THEMES[DEFAULT_TEMPLATE];

            return (
              <div
                key={dossier.id}
                className="sd-profile-card"
                onClick={() => navigate(`/dossiers/${dossier.id}/edit`)}
              >
                {/* Header: avatar + name + title */}
                <div className="sd-card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                    <div className="sd-profile-av" style={{ background: bg }}>{trigram}</div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      {editingId === dossier.id ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={(e) => e.stopPropagation()}>
                          <input
                            autoFocus
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveRename(dossier);
                              else if (e.key === 'Escape') cancelRename();
                            }}
                            disabled={savingId === dossier.id}
                            placeholder="Nom du consultant"
                            style={{
                              flex: 1, minWidth: 0,
                              fontSize: 14, fontWeight: 600,
                              padding: '3px 7px', borderRadius: 5,
                              background: 'var(--bg)', color: 'var(--white)',
                              border: '1px solid var(--vio)', outline: 'none',
                            }}
                          />
                          <button className="sd-action-btn" title="Enregistrer" onClick={() => saveRename(dossier)} disabled={savingId === dossier.id} style={{ padding: '4px 6px' }}>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 6.5l2.5 2.5 4.5-5.5"/></svg>
                          </button>
                          <button className="sd-action-btn" title="Annuler" onClick={cancelRename} disabled={savingId === dossier.id} style={{ padding: '4px 6px' }}>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 3l6 6M9 3l-6 6"/></svg>
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="sd-profile-name" style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || '—'}</span>
                            <button
                              className="sd-action-btn"
                              title="Renommer le consultant"
                              onClick={(e) => startRename(dossier, e)}
                              style={{ padding: 3, flexShrink: 0, border: 'none', background: 'transparent', color: 'var(--muted2)' }}
                            >
                              <IconEdit />
                            </button>
                          </div>
                          <div className="sd-profile-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {dossier.consultant_title || '—'}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Template chip — top right */}
                  <div
                    title={`Template : ${tmpl.label}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '3px 7px', borderRadius: 5,
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid var(--border)',
                      flexShrink: 0,
                    }}
                  >
                    <span style={{
                      width: 8, height: 8, borderRadius: 2,
                      background: tmpl.coverBg && String(tmpl.coverBg).startsWith('linear')
                        ? tmpl.coverBg
                        : tmpl.pageBorder,
                      flexShrink: 0,
                      border: '1px solid rgba(255,255,255,0.06)',
                    }} />
                    <span style={{ fontSize: 9.5, color: 'var(--muted2)', fontWeight: 600, letterSpacing: 0.2 }}>
                      {tmpl.label.length > 14 ? tmpl.label.slice(0, 12) + '…' : tmpl.label}
                    </span>
                  </div>
                </div>

                {/* Meta: stats row */}
                <div style={{
                  display: 'flex', flexWrap: 'wrap', gap: 12,
                  margin: '8px 0 10px',
                  fontSize: 11, color: 'var(--muted2)',
                }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                      <rect x="1" y="3.5" width="9" height="6.5" rx="1"/>
                      <path d="M3.5 3.5V2.5a1.5 1.5 0 013 0v1"/>
                    </svg>
                    <strong style={{ color: 'var(--white)', fontWeight: 600 }}>{expCount}</strong> expérience{expCount !== 1 ? 's' : ''}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5.5 1.5l4.5 2-4.5 2L1 3.5z"/><path d="M2 5v2.5c0 .5 1.5 1.5 3.5 1.5s3.5-1 3.5-1.5V5"/>
                    </svg>
                    <strong style={{ color: 'var(--white)', fontWeight: 600 }}>{forCount}</strong> formation{forCount !== 1 ? 's' : ''}
                  </span>
                  {comps.length > 0 && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                        <circle cx="5.5" cy="5.5" r="4.5"/>
                        <path d="M3.5 5.5l1.5 1.5 2.5-2.5"/>
                      </svg>
                      <strong style={{ color: 'var(--white)', fontWeight: 600 }}>{comps.length}</strong> compétence{comps.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {/* Competence chips (up to 5) */}
                {comps.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    {comps.slice(0, 5).map((c, i) => (
                      <span key={i} className="sd-chip">
                        {typeof c === 'string' ? c : (c.domain || c.name || c.label || '')}
                      </span>
                    ))}
                    {comps.length > 5 && (
                      <span className="sd-chip" style={{ opacity: 0.6 }}>+{comps.length - 5}</span>
                    )}
                  </div>
                )}

                {/* Footer: last-touched timestamp */}
                <div style={{
                  fontSize: 10.5, color: 'var(--muted)',
                  marginBottom: 8,
                  paddingBottom: 8,
                  borderBottom: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', gap: 4,
                }} title={lastTouched ? new Date(lastTouched).toLocaleString('fr-FR') : ''}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                    <circle cx="5" cy="5" r="4"/><path d="M5 2.5V5l1.5 1"/>
                  </svg>
                  {lastTouchedLabel}
                </div>

                {/* Actions */}
                <div className="sd-card-actions" style={{ paddingTop: 0, borderTop: 'none' }} onClick={(e) => e.stopPropagation()}>
                  <button className="sd-action-btn" title="Télécharger PDF" onClick={() => handleDownloadPDF(dossier)}>
                    <IconDownload />PDF
                  </button>
                  <button className="sd-action-btn" title="Modifier" onClick={() => navigate(`/dossiers/${dossier.id}/edit`)}>
                    <IconEdit />Modifier
                  </button>
                  <button className="sd-action-btn danger" title="Supprimer" onClick={() => handleDelete(dossier.id)}>
                    <IconTrash />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
};

export default Library;
